/**
 * gesturePolicy — play-time spatial admissibility for generated performance
 * clips.
 *
 * QC on the server no longer bounds how FAR a performance may travel (only
 * how fast — implausible root speed is a generation glitch and rejects at
 * generation time). Whether the travel fits the scene is decided HERE, at
 * play time, by the only party that knows the scene: the renderer checks the
 * clip's root path against the loaded navmesh and rejects the performance
 * outright when its mid/end points leave walkable space. No rescaling, no
 * clamping — an inadmissible clip simply doesn't play (deliberate: a scaled
 * walk desyncs from its motion, and a clamped one walks in place into a
 * wall).
 *
 * No navmesh loaded → every clip is admissible (scenes without navmeshes
 * have no walkability data to enforce; this is also every scene before
 * ENG-592 wayfinding is configured).
 */
import { snapToNavmesh } from './bodyMotionPath.js'

// How far (m) a sampled path point may sit from its nearest navmesh node and
// still count as on-mesh. snapToNavmesh measures to node CENTERS, so even a
// perfectly walkable point sits ~half a cell away; 0.75m tolerates that plus
// mesh-edge slack without letting a point in open void pass.
const DEFAULT_MAX_SNAP_DIST = 0.75

// Fractions of the clip at which the root path is validated. Mid + end per
// the taxonomy policy decision: end catches "walks off the stage", mid
// catches an out-and-back arc whose far point is off-mesh.
const DEFAULT_SAMPLES = [0.5, 1.0]

/**
 * Validate a performance clip's root travel against the navmesh.
 * @param {object} tracks kimodo payload { root?: [x,y,z,...], frames? }
 * @param {{x:number,z:number}|null} avatarPos avatar group world position the
 *   clip will play FROM (deltas are relative to frame 0)
 * @param {{nearestNode:Function,nodes:Array}|null} nm NavMesh instance, or
 *   null/undefined when no navmesh is loaded
 * @param {object} [opts] { samples, maxSnapDist }
 * @returns {{ok: boolean, reason?: string}}
 */
export function validateTravelOnNavmesh(tracks, avatarPos, nm, opts = {}) {
  if (!nm) return { ok: true } // no walkability data — nothing to enforce
  const root = tracks?.root
  const frames = tracks?.frames || (root ? Math.floor(root.length / 3) : 0)
  if (!root || frames < 2) return { ok: true } // in-place clip — no travel to validate

  const samples = opts.samples || DEFAULT_SAMPLES
  const maxSnapDist = opts.maxSnapDist ?? DEFAULT_MAX_SNAP_DIST
  const baseX = avatarPos?.x ?? 0
  const baseZ = avatarPos?.z ?? 0
  const r0x = root[0]
  const r0z = root[2]

  for (const f of samples) {
    const idx = Math.min(frames - 1, Math.max(0, Math.round(f * (frames - 1))))
    // Clip-space delta from frame 0 (world meters — kimodo root is world-
    // space), applied at the avatar's current position.
    const x = baseX + (root[idx * 3] - r0x)
    const z = baseZ + (root[idx * 3 + 2] - r0z)
    const { dist } = snapToNavmesh(nm, x, z)
    if (dist > maxSnapDist) {
      return {
        ok: false,
        reason: `path point at ${Math.round(f * 100)}% (${x.toFixed(2)}, ${z.toFixed(2)}) is ${dist === Infinity ? 'off' : dist.toFixed(2) + 'm off'} the navmesh`,
      }
    }
  }
  return { ok: true }
}
